import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Users, Ban, Flag, RefreshCw, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { adminApi, Player, PlayerAggregatedMetrics, PlayerRelationshipGraph } from '../../services/admin-api';

export function PlayerDetail() {
  const { id } = useParams<{ id: string }>();
  
  const [player, setPlayer] = useState<Player | null>(null);
  const [analytics, setAnalytics] = useState<PlayerAggregatedMetrics | null>(null);
  const [relationshipGraph, setRelationshipGraph] = useState<PlayerRelationshipGraph | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Modal states
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showFlagModal, setShowFlagModal] = useState(false);
  const [suspendReason, setSuspendReason] = useState('');
  const [flagType, setFlagType] = useState<'warning' | 'review' | 'violation'>('warning');
  const [flagReason, setFlagReason] = useState('');
  
  // Active tab for charts
  const [activeTab, setActiveTab] = useState<'overview' | 'engagement' | 'graph'>('overview');

  const loadPlayerData = async () => {
    if (!id) return;
    
    try {
      setLoading(true);
      const [playerData, analyticsData, graphData] = await Promise.all([
        adminApi.players.get(id),
        adminApi.players.getAnalytics(id).catch(() => null),
        adminApi.players.getRelationshipGraph(id).catch(() => null),
      ]);
      
      setPlayer(playerData);
      setAnalytics(analyticsData);
      setRelationshipGraph(graphData);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load player');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPlayerData();
  }, [id]);

  const handleSuspend = async () => {
    if (!id || !suspendReason.trim()) return;
    
    try {
      await adminApi.players.suspend(id, suspendReason);
      setShowSuspendModal(false);
      setSuspendReason('');
      await loadPlayerData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to suspend player');
    }
  };

  const handleUnsuspend = async () => {
    if (!id) return;
    
    try {
      await adminApi.players.unsuspend(id);
      await loadPlayerData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to unsuspend player');
    }
  };

  const handleAddFlag = async () => {
    if (!id || !flagReason.trim()) return;
    
    try {
      await adminApi.players.addFlag(id, flagType, flagReason);
      setShowFlagModal(false);
      setFlagReason('');
      await loadPlayerData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to add flag');
    }
  };

  const handleRemoveFlag = async (flagIndex: number) => {
    if (!id || !confirm('Remove this flag?')) return;
    
    try {
      await adminApi.players.removeFlag(id, flagIndex);
      await loadPlayerData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to remove flag');
    }
  };

  const handleResetGameState = async () => {
    if (!id || !confirm('Reset player game state? This cannot be undone.')) return;
    
    try {
      await adminApi.players.reset(id);
      await loadPlayerData();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Failed to reset game state');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'inactive':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
      case 'suspended':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'banned':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200';
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatPlaytime = (ms: number) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return `${hours}h ${minutes}m`;
  };

  const getTrendIcon = (trend: string) => {
    switch (trend) {
      case 'increasing':
        return <TrendingUp className="w-4 h-4 text-green-600" />;
      case 'declining':
        return <TrendingDown className="w-4 h-4 text-red-600" />;
      default:
        return <Minus className="w-4 h-4 text-gray-600" />;
    }
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-gray-600 dark:text-gray-400">Loading player...</div>
      </div>
    );
  }

  if (error || !player) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center text-red-600 dark:text-red-400">{error || 'Player not found'}</div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <Link
        to="/players"
        className="inline-flex items-center text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mb-6"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Back to Players
      </Link>

      <div className="flex items-center gap-3 mb-6">
        <Users className="w-8 h-8 text-blue-600 dark:text-blue-400" />
        <div className="flex-1">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{player.displayName}</h1>
          <p className="text-gray-600 dark:text-gray-400">{player.email}</p>
        </div>
        <span className={`px-3 py-1 text-sm font-semibold rounded-full ${getStatusColor(player.status)}`}>
          {player.status}
        </span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Player Info & Actions */}
        <div className="lg:col-span-1 space-y-6">
          {/* Player Info Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Player Info</h2>
            <dl className="space-y-3 text-sm">
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Player ID</dt>
                <dd className="text-gray-900 dark:text-white font-mono text-xs">{player.id}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">User ID</dt>
                <dd className="text-gray-900 dark:text-white font-mono text-xs">{player.userId}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Created</dt>
                <dd className="text-gray-900 dark:text-white">{new Date(player.createdAt).toLocaleDateString()}</dd>
              </div>
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Last Updated</dt>
                <dd className="text-gray-900 dark:text-white">{new Date(player.updatedAt).toLocaleDateString()}</dd>
              </div>
              {player.lastActiveAt && (
                <div>
                  <dt className="text-gray-500 dark:text-gray-400">Last Active</dt>
                  <dd className="text-gray-900 dark:text-white">{new Date(player.lastActiveAt).toLocaleDateString()}</dd>
                </div>
              )}
              <div>
                <dt className="text-gray-500 dark:text-gray-400">Total Spent</dt>
                <dd className="text-gray-900 dark:text-white font-semibold">{formatCurrency(player.totalSpent || 0)}</dd>
              </div>
            </dl>
          </div>

          {/* Admin Actions Card */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Admin Actions</h2>
            <div className="space-y-3">
              {player.status === 'active' ? (
                <button
                  onClick={() => setShowSuspendModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 transition-colors"
                >
                  <Ban className="w-4 h-4" />
                  Suspend Player
                </button>
              ) : (
                <button
                  onClick={handleUnsuspend}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  <Ban className="w-4 h-4" />
                  Unsuspend Player
                </button>
              )}
              
              <button
                onClick={() => setShowFlagModal(true)}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
              >
                <Flag className="w-4 h-4" />
                Add Flag
              </button>
              
              <button
                onClick={handleResetGameState}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Reset Game State
              </button>
            </div>
            
            {player.suspensionReason && (
              <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded">
                <p className="text-xs font-semibold text-yellow-800 dark:text-yellow-200 mb-1">Suspension Reason:</p>
                <p className="text-sm text-yellow-700 dark:text-yellow-300">{player.suspensionReason}</p>
              </div>
            )}
          </div>

          {/* Flags Section */}
          {player.flags && player.flags.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Flags</h2>
              <div className="space-y-3">
                {player.flags.map((flag, index) => (
                  <div key={index} className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <span className="inline-block px-2 py-1 text-xs font-semibold rounded bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 mb-2">
                          {flag.type}
                        </span>
                        <p className="text-sm text-gray-900 dark:text-white">{flag.reason}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {new Date(flag.createdAt).toLocaleDateString()} by {flag.createdBy}
                        </p>
                      </div>
                      <button
                        onClick={() => handleRemoveFlag(index)}
                        className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Metrics & Charts */}
        <div className="lg:col-span-2 space-y-6">
          {/* Engagement Metrics Grid */}
          {analytics && (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">Characters Created</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.charactersCreated}</div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">Games Played</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.gamesPlayed}</div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">Total Turns</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.totalTurns}</div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">Novels Generated</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{analytics.novelsGenerated}</div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="text-sm text-gray-500 dark:text-gray-400">Total Playtime</div>
                <div className="text-2xl font-bold text-gray-900 dark:text-white">{formatPlaytime(analytics.totalPlaytimeMs)}</div>
              </div>
              
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
                <div className="text-sm text-gray-500 dark:text-gray-400 mb-1">Engagement Trend</div>
                <div className="flex items-center gap-2">
                  {getTrendIcon(analytics.lastEngagementTrend)}
                  <span className="text-lg font-semibold text-gray-900 dark:text-white capitalize">{analytics.lastEngagementTrend}</span>
                </div>
              </div>
            </div>
          )}

          {/* Charts Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
            {/* Tab Navigation */}
            <div className="border-b border-gray-200 dark:border-gray-700">
              <nav className="flex -mb-px">
                <button
                  onClick={() => setActiveTab('overview')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'overview'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Overview
                </button>
                <button
                  onClick={() => setActiveTab('engagement')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'engagement'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Engagement
                </button>
                <button
                  onClick={() => setActiveTab('graph')}
                  className={`px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                    activeTab === 'graph'
                      ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  Relationship Graph
                </button>
              </nav>
            </div>

            {/* Tab Content */}
            <div className="p-6">
              {activeTab === 'overview' && analytics && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Genre Distribution</h3>
                  {Object.keys(analytics.genreDistribution).length > 0 ? (
                    <div className="space-y-2">
                      {Object.entries(analytics.genreDistribution).map(([genre, count]) => (
                        <div key={genre} className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 dark:text-gray-400 capitalize">{genre}</span>
                          <span className="text-sm font-semibold text-gray-900 dark:text-white">{count}</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No genre data available yet</p>
                  )}
                </div>
              )}

              {activeTab === 'engagement' && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Engagement Timeline</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Engagement timeline chart coming soon...
                  </p>
                </div>
              )}

              {activeTab === 'graph' && relationshipGraph && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    Player Relationship Network
                  </h3>
                  {relationshipGraph.nodes.length > 0 ? (
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded">
                          <div className="text-xs text-gray-500 dark:text-gray-400">Total Nodes</div>
                          <div className="text-xl font-bold text-gray-900 dark:text-white">{relationshipGraph.nodes.length}</div>
                        </div>
                        <div className="p-3 bg-gray-50 dark:bg-gray-900 rounded">
                          <div className="text-xs text-gray-500 dark:text-gray-400">Total Connections</div>
                          <div className="text-xl font-bold text-gray-900 dark:text-white">{relationshipGraph.edges.length}</div>
                        </div>
                      </div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Interactive relationship graph visualization coming soon...
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400">No relationship data available yet</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Suspend Modal */}
      {showSuspendModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Suspend Player</h3>
            <textarea
              value={suspendReason}
              onChange={(e) => setSuspendReason(e.target.value)}
              placeholder="Enter suspension reason (min 10 characters)..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowSuspendModal(false);
                  setSuspendReason('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleSuspend}
                disabled={suspendReason.length < 10}
                className="flex-1 px-4 py-2 bg-yellow-600 text-white rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suspend
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Flag Modal */}
      {showFlagModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Add Flag</h3>
            <select
              value={flagType}
              onChange={(e) => setFlagType(e.target.value as 'warning' | 'review' | 'violation')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white mb-3"
            >
              <option value="warning">Warning</option>
              <option value="review">Review</option>
              <option value="violation">Violation</option>
            </select>
            <textarea
              value={flagReason}
              onChange={(e) => setFlagReason(e.target.value)}
              placeholder="Enter flag reason (min 10 characters)..."
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md bg-white dark:bg-gray-900 text-gray-900 dark:text-white mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowFlagModal(false);
                  setFlagReason('');
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleAddFlag}
                disabled={flagReason.length < 10}
                className="flex-1 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Add Flag
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
